import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Download, Loader2, FileText } from 'lucide-react'
import { startupsApi } from '@/services/api'
import { questionsApi } from '@/services/questions'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { StartupAnalysisBanner } from '@/components/startup-analysis-banner'
import { useToast } from '@/hooks/use-toast'
import { jsPDF } from 'jspdf'
import {
  loadLogoForPdf, C, setRgb, fillRgb, PW, PH, ML, MR, MT,
  addPageChrome, RenderCtx, ensureSpace, renderH2, renderParagraph, renderNumbered
} from '@/lib/pdf-utils'

const headingFont = { fontFamily: '"Outfit", "Inter", sans-serif' }

const categoryNames: Record<string, string> = {
  team: 'Team & Execution',
  market: 'Market Dynamics',
  product: 'Product Strategy',
  competition: 'Competitive Landscape',
  financials: 'Financials & Metrics',
  business_model: 'Business Model',
}

const priorityColors: Record<string, string> = {
  high: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
  medium: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
  low: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
}

async function generateCheatSheetPdf(
  startup: any,
  groupedQuestions: Record<string, any[]>,
) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  let logoData: { data: string; width: number; height: number } | null = null
  try { logoData = await loadLogoForPdf() } catch { /* no logo */ }

  const companyName = startup.profile.company_info.name
  const date = new Date().toLocaleDateString()
  const docType = 'MEETING CHEAT SHEET'

  addPageChrome(doc, companyName, date, 1, 1, logoData, docType)

  const ctx: RenderCtx = {
    doc, y: MT + 14, companyName, date,
    pageNum: 1, totalPages: 1, logoData,
    documentType: docType
  }

  // Draw Cheat Sheet Title
  ensureSpace(ctx, 20)
  
  // Startup Name as main title
  doc.setFontSize(24)
  doc.setFont('helvetica', 'bold')
  setRgb(doc, C.navy)
  doc.text(companyName.toUpperCase(), ML, ctx.y)
  ctx.y += 8
  
  // "Meeting Cheat Sheet" as subtitle
  doc.setFontSize(14)
  setRgb(doc, C.slate500)
  doc.text('Meeting Cheat Sheet', ML, ctx.y)
  ctx.y += 8

  const description = startup.profile.company_info.short_description || startup.profile.company_info.description
  if (description && description.toLowerCase() !== 'na' && description.toLowerCase() !== 'n/a') {
    renderParagraph(ctx, description)
    ctx.y += 4
  }

  // Draw categories
  if (Object.keys(groupedQuestions).length === 0) {
    renderParagraph(ctx, "No questions generated yet for this startup.")
  } else {
    for (const [category, catQuestions] of Object.entries(groupedQuestions)) {
      renderH2(ctx, categoryNames[category] || category.charAt(0).toUpperCase() + category.slice(1))
      catQuestions.forEach((q, idx) => {
        renderNumbered(ctx, idx + 1, q.question_text)
      })
      ctx.y += 6
    }
  }

  // Fix page numbers
  const finalPageCount = doc.getNumberOfPages()
  for (let p = 1; p <= finalPageCount; p++) {
    doc.setPage(p)
    fillRgb(doc, C.offWhite)
    doc.rect(PW - MR - 20, PH - 10, 22, 8, 'F')
    doc.setFontSize(6.5)
    doc.setFont('helvetica', 'normal')
    setRgb(doc, C.slate500)
    doc.text(`${p} / ${finalPageCount}`, PW - MR, PH - 5.5, { align: 'right' })
  }

  return doc
}

export function CheatSheet() {
  const { id } = useParams<{ id: string }>()
  const { toast } = useToast()
  const [isDownloading, setIsDownloading] = useState(false)

  const { data: startup, isLoading: isStartupLoading } = useQuery({
    queryKey: ['startup', id],
    queryFn: () => startupsApi.get(id!),
    enabled: !!id,
  })

  const { data: questions, isLoading: isQuestionsLoading } = useQuery({
    queryKey: ['startup-questions', id],
    queryFn: () => questionsApi.getStartupQuestions(id!),
    enabled: !!id,
  })

  if (isStartupLoading || isQuestionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!startup) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Startup not found</h2>
        <Button asChild>
          <Link to="/startups">Return to Startups</Link>
        </Button>
      </div>
    )
  }

  // Group questions by category and sort by priority (high > medium > low)
  const groupedQuestions: Record<string, any[]> = {}
  
  if (questions) {
    const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 }
    
    // Sort all questions first
    const sortedQuestions = [...questions].sort((a, b) => {
      const pA = priorityOrder[a.priority] ?? 3
      const pB = priorityOrder[b.priority] ?? 3
      return pA - pB
    })

    sortedQuestions.forEach(q => {
      const cat = q.category || 'other'
      if (!groupedQuestions[cat]) groupedQuestions[cat] = []
      groupedQuestions[cat].push(q)
    })
  }

  const handleDownloadPdf = async () => {
    setIsDownloading(true)
    try {
      const doc = await generateCheatSheetPdf(startup, groupedQuestions)
      const filename = `${startup.profile.company_info.name.replace(/[^a-zA-Z0-9]/g, '_')}_Cheat_Sheet.pdf`
      doc.save(filename)
      toast({ title: 'PDF downloaded', description: filename })
    } catch (err) {
      console.error('PDF generation error:', err)
      toast({ title: 'Download failed', description: String(err), variant: 'destructive' })
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-20 pt-6">
      <div className="max-w-7xl mx-auto px-4 mb-6">
        <StartupAnalysisBanner />
      </div>

      <div className="max-w-6xl mx-auto px-4">
        <Card className="border-0 shadow-none bg-transparent">
          <CardContent className="p-0">
            {/* Document on a desk effect */}
            <div className="bg-slate-200/50 dark:bg-slate-900/50 p-4 sm:p-8 rounded-xl">
              <div className="max-w-4xl mx-auto bg-white dark:bg-[#0F172A] shadow-2xl rounded-sm overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col" style={{ height: '800px' }}>

                {/* Sticky header */}
                <div className="sticky top-0 z-10 flex-shrink-0 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 px-8 py-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <h4
                          className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight"
                          style={headingFont}
                        >
                          {startup.profile.company_info.name}
                        </h4>
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-bold text-slate-600 dark:text-slate-400">
                          {startup.profile.company_info.industry || 'Startup'}
                        </span>
                      </div>
                      <p className="text-lg font-bold text-slate-500 dark:text-slate-400 mt-1">
                        Meeting Cheat Sheet
                      </p>
                      {startup.profile.company_info.short_description && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 max-w-2xl leading-relaxed">
                          {startup.profile.company_info.short_description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <img src="/logo.png" alt="uniscout.ai" className="w-14 h-14 object-contain" />
                      <Button
                        onClick={handleDownloadPdf}
                        disabled={isDownloading}
                        size="sm"
                        className="bg-gradient-to-r from-[#4194F2] to-[#E91F84] text-white hover:opacity-90 transition-opacity gap-2"
                      >
                        {isDownloading ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                        ) : (
                          <><Download className="w-4 h-4" /> Download PDF</>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto px-8 py-8">
                  <article className="prose prose-slate dark:prose-invert max-w-none font-['Inter',_system-ui,_sans-serif]">
                    
                    <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed mb-8 text-justify">
                      {startup.profile.company_info.description}
                    </p>

                    {Object.keys(groupedQuestions).length === 0 ? (
                      <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                        No questions generated yet for this startup. Run an analysis first.
                      </div>
                    ) : (
                      <div className="space-y-10">
                        {Object.entries(groupedQuestions).map(([category, catQuestions]) => (
                          <section key={category}>
                            <h2 className="text-2xl font-black tracking-tight text-slate-800 dark:text-slate-200 mt-10 mb-4 border-b border-slate-200 dark:border-slate-800 pb-2" style={headingFont}>
                              {categoryNames[category] || category.charAt(0).toUpperCase() + category.slice(1)}
                            </h2>
                            <div className="space-y-4">
                              {catQuestions.map((q, idx) => (
                                <div key={q.id || idx} className="flex gap-4 items-start bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-100 dark:border-slate-800">
                                  <div className="flex-shrink-0 font-bold text-slate-400 dark:text-slate-500 text-lg w-6 pt-0.5">
                                    {idx + 1}.
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-base font-medium text-slate-800 dark:text-slate-200 mb-2 leading-relaxed">
                                      {q.question_text}
                                    </p>
                                    <div className="flex gap-2">
                                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-md border ${priorityColors[q.priority] || priorityColors.medium}`}>
                                        {q.priority} priority
                                      </span>
                                      {q.is_ai_generated && (
                                        <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-md border bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800">
                                          AI Generated
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </section>
                        ))}
                      </div>
                    )}

                  </article>
                </div>

                {/* Sticky footer */}
                <div className="sticky bottom-0 flex-shrink-0 bg-slate-50 dark:bg-slate-800/50 px-8 py-4 border-t border-slate-100 dark:border-slate-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img src="/logo.png" alt="uniscout.ai" className="w-9 h-9 object-contain" />
                      <div>
                        <span
                          className="text-sm font-black logo-gradient-text"
                          style={headingFont}
                        >
                          uniscout.ai
                        </span>
                        <span className="text-[10px] text-slate-500 dark:text-slate-400 ml-2 font-bold uppercase tracking-wider">
                          AI-Powered Investment Analysis
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <FileText className="w-3.5 h-3.5 text-slate-400" />
                      <p className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest">
                        Confidential — Internal Document
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
